import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Spinner, Modal, ModalBody, ModalTitle, VTooltip, cn, Textarea } from "@vertesia/ui/core";
import { Activity, FileTextIcon, HelpCircleIcon, PaperclipIcon, SendIcon, StopCircleIcon, UploadIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FileProcessingStatus } from "@vertesia/common";
import { SelectDocument } from "../../../store";
import { useUITranslation } from "../../../../i18n/index.js";
export default function MessageInput({ onSend, onStop, disabled = false, isSending = false, isStopping = false, isStreaming = false, isCompleted = false, activeTaskCount = 0, placeholder, 
// File upload props
onFilesSelected, uploadedFiles = [], onRemoveFile, acceptedFileTypes, maxFiles = 5, processingFiles, hasProcessingFiles = false, 
// Document search props
renderDocumentSearch, selectedDocuments = [], onRemoveDocument, 
// Object linking
hideObjectLinking = false, 
// File upload
hideFileUpload = false, 
// Styling props
className, }) {
    const { t } = useUITranslation();
    const resolvedPlaceholder = placeholder ?? t('agent.typeYourMessage');
    const ref = useRef(null);
    const fileInputRef = useRef(null);
    const [value, setValue] = useState("");
    const [isObjectModalOpen, setIsObjectModalOpen] = useState(false);
    const [isDocSearchOpen, setIsDocSearchOpen] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    useEffect(() => {
        if (!disabled && isCompleted)
            ref.current?.focus();
    }, [disabled, isCompleted]);
    // File handling
    const handleFiles = useCallback((files) => {
        if (!onFilesSelected)
            return;
        const fileArray = Array.from(files);
        const remainingSlots = maxFiles - uploadedFiles.length;
        const filesToAdd = fileArray.slice(0, remainingSlots);
        if (filesToAdd.length > 0) {
            onFilesSelected(filesToAdd);
        }
    }, [onFilesSelected, maxFiles, uploadedFiles.length]);
    // Drag and drop handlers
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onFilesSelected) {
            setIsDragOver(true);
        }
    }, [onFilesSelected]);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    }, [handleFiles]);
    // Paste handler for files
    const handlePaste = useCallback((e) => {
        if (!onFilesSelected)
            return;
        const items = e.clipboardData?.items;
        if (!items)
            return;
        const files = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                    // If it's an image without a proper name, generate one
                    if (item.type.startsWith('image/') && (!file.name || file.name === 'image.png')) {
                        const extension = item.type.split('/')[1] || 'png';
                        const namedFile = new File([file], `pasted-image-${Date.now()}.${extension}`, {
                            type: file.type,
                        });
                        files.push(namedFile);
                    }
                    else {
                        files.push(file);
                    }
                }
            }
        }
        if (files.length > 0) {
            handleFiles(files);
        }
    }, [onFilesSelected, handleFiles]);
    // File input change handler
    const handleFileInputChange = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
            // Reset input so same file can be selected again
            e.target.value = '';
        }
    }, [handleFiles]);
    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    // // Document search handlers
    // const handleDocumentSelect = useCallback((doc: SelectedDocument) => {
    //     // Insert document reference into message
    //     const markdownLink = `[📄 ${doc.name}](doc:${doc.id})`;
    //     const currentValue = value || '';
    //     const cursorPos = ref.current?.selectionStart || currentValue.length;
    //     const newValue = currentValue.substring(0, cursorPos) + markdownLink + currentValue.substring(cursorPos);
    //     setValue(newValue);
    //     setIsDocSearchOpen(false);
    // }, [value]);
    const handleDocSearchClose = useCallback(() => setIsDocSearchOpen(false), []);
    const handleDocSearchSelect = useCallback((_doc) => setIsDocSearchOpen(false), []);
    const handleSend = () => {
        const message = value.trim();
        if (!message || disabled || isSending)
            return;
        onSend(message);
        setValue("");
    };
    const handleStop = () => {
        if (onStop && !isStopping) {
            onStop();
        }
    };
    // Track Escape key presses for double-tap to stop
    const lastEscapeRef = useRef(0);
    const keyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const hasMessage = value.trim().length > 0;
            if (hasMessage) {
                // If there's a message, send it (this will interrupt + send message via UserInput signal)
                handleSend();
            }
            // Enter with no text does nothing (don't stop, don't send)
        }
        // Double Escape to stop the agent
        if (e.key === "Escape" && isStreaming && onStop) {
            const now = Date.now();
            if (now - lastEscapeRef.current < 500) {
                // Double Escape within 500ms - stop the agent
                handleStop();
                lastEscapeRef.current = 0;
            }
            else {
                lastEscapeRef.current = now;
            }
        }
        // Shift+Enter allows newline (default textarea behavior)
    };
    // Auto-resize textarea as content grows
    const adjustTextareaHeight = useCallback(() => {
        const textarea = ref.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        }
    }, []);
    useEffect(() => {
        adjustTextareaHeight();
    }, [value, adjustTextareaHeight]);
    const handleObjectSelect = (object) => {
        // Create a markdown link with the object title and ID
        const objectTitle = object.properties?.title || object.name || 'Object';
        const objectId = object.id;
        const markdownLink = `[${objectTitle}](store:${objectId})`;
        // Insert the link at cursor position or append to end
        const currentValue = value || '';
        const cursorPos = ref.current?.selectionStart || currentValue.length;
        const newValue = currentValue.substring(0, cursorPos) + markdownLink + currentValue.substring(cursorPos);
        // Update the input value
        setValue(newValue);
        // Close the modal
        setIsObjectModalOpen(false);
        // Focus back on the input
        setTimeout(() => {
            if (ref.current) {
                ref.current.focus();
                // Place cursor after the inserted link
                const newCursorPos = cursorPos + markdownLink.length;
                ref.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 100);
    };
    return (_jsxs("div", { className: cn("p-3 border-t border-muted flex-shrink-0 transition-all fixed lg:sticky bottom-0 left-0 right-0 lg:left-auto lg:right-auto w-full bg-background z-10", isDragOver && "bg-blue-50 dark:bg-blue-900/20 border-blue-400", className), style: { minHeight: "120px" }, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [isDragOver && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-blue-100/80 dark:bg-blue-900/40 rounded-lg z-10 pointer-events-none", children: _jsxs("div", { className: "text-blue-600 dark:text-blue-400 font-medium flex items-center gap-2", children: [_jsx(UploadIcon, { className: "size-5" }), t('agent.dropFilesToUpload')] }) })), onFilesSelected && (_jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: acceptedFileTypes, onChange: handleFileInputChange, className: "hidden" })), !hideFileUpload && (uploadedFiles.length > 0 || (processingFiles && processingFiles.size > 0)) && (_jsx("div", { className: "flex flex-col gap-2 mb-3", children: _jsxs("div", { children: [_jsxs("div", { className: "flex items-center gap-1 mb-1", children: [_jsx("span", { className: "text-xs font-medium text-gray-500 dark:text-gray-400", children: t('agent.uploadedFiles') }), _jsx(VTooltip, { description: t('agent.filesUploadedDescription'), placement: "top", size: "md", children: _jsx(HelpCircleIcon, { className: "size-3 text-gray-400 dark:text-gray-500" }) })] }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [processingFiles && Array.from(processingFiles.values()).map((file) => (_jsxs("div", { className: cn("flex items-center gap-1.5 px-2 py-1 rounded-md text-sm", file.status === FileProcessingStatus.ERROR
                                        ? "bg-destructive/10 text-destructive"
                                        : file.status === FileProcessingStatus.READY
                                            ? "bg-success/10 text-success"
                                            : "bg-attention/10 text-attention"), children: [_jsx(FileTextIcon, { className: cn("size-3.5", (file.status === FileProcessingStatus.UPLOADING || file.status === FileProcessingStatus.PROCESSING) && "animate-pulse") }), _jsx("span", { className: "max-w-[120px] truncate", children: file.name }), _jsx("span", { className: "text-xs opacity-70", children: file.status === FileProcessingStatus.UPLOADING ? t('agent.uploading')
                                                : file.status === FileProcessingStatus.PROCESSING ? t('agent.processing')
                                                    : file.status === FileProcessingStatus.ERROR ? t('agent.error')
                                                        : file.status === FileProcessingStatus.READY ? t('agent.ready') : file.status })] }, file.id))), uploadedFiles.map((file) => (_jsxs("div", { className: "flex items-center gap-1.5 px-2 py-1 bg-success/10 text-success rounded-md text-sm", children: [_jsx(FileTextIcon, { className: "size-3.5" }), _jsx("span", { className: "max-w-[120px] truncate", children: file.name }), onRemoveFile && (_jsx("button", { onClick: () => onRemoveFile(file.id), className: "ml-1 p-0.5 hover:bg-success/20 rounded", children: _jsx(XIcon, { className: "size-3" }) }))] }, file.id)))] })] }) })), selectedDocuments.length > 0 && (_jsxs("div", { className: "mb-3", children: [_jsxs("div", { className: "flex items-center gap-1 mb-1", children: [_jsx("span", { className: "text-xs font-medium text-blue-600 dark:text-blue-400", children: t('agent.documentAttachments') }), _jsx(VTooltip, { description: t('agent.documentAttachmentsDescription'), placement: "top", size: "md", children: _jsx(HelpCircleIcon, { className: "size-3 text-blue-400 dark:text-blue-500" }) })] }), _jsx("div", { className: "flex flex-wrap gap-2", children: selectedDocuments.map((doc) => (_jsxs("div", { className: "flex items-center gap-1.5 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-md text-sm text-blue-700 dark:text-blue-300", children: [_jsx(FileTextIcon, { className: "size-3.5" }), _jsx("span", { className: "max-w-[120px] truncate", children: doc.name }), onRemoveDocument && (_jsx("button", { onClick: () => onRemoveDocument(doc.id), className: "ml-1 p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded", children: _jsx(XIcon, { className: "size-3" }) }))] }, doc.id))) })] })), (onFilesSelected || renderDocumentSearch) && (_jsxs("div", { className: "flex gap-2 mb-2", children: [onFilesSelected && (_jsxs(Button, { variant: "outline", size: "sm", onClick: openFileDialog, disabled: disabled || uploadedFiles.length >= maxFiles, className: "text-xs", children: [_jsx(UploadIcon, { className: "size-3.5 mr-1.5" }), t('agent.upload')] })), renderDocumentSearch && (_jsxs(Button, { variant: "outline", size: "sm", onClick: () => setIsDocSearchOpen(true), disabled: disabled, className: "text-xs", children: [_jsx(FileTextIcon, { className: "size-3.5 mr-1.5" }), t('agent.searchDocuments'), selectedDocuments.length > 0 && (_jsx("span", { className: "ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-600 text-white", children: selectedDocuments.length }))] }))] })), _jsxs("div", { className: "flex items-end space-x-2", children: [_jsxs("div", { className: "flex flex-1 items-end space-x-1", children: [_jsx(Textarea, { ref: ref, value: value, onKeyDown: keyDown, onChange: (e) => setValue(e.target.value), onPaste: handlePaste, disabled: disabled, placeholder: isStreaming ? t('agent.agentWorking') : (onFilesSelected ? t('agent.askAnything') : resolvedPlaceholder), rows: 2, style: { minHeight: '60px', maxHeight: '200px' } }), !hideObjectLinking && (_jsx(Button, { variant: "ghost", className: "rounded-full", disabled: !isCompleted, onClick: () => setIsObjectModalOpen(true), alt: t('agent.linkObject'), children: _jsx(PaperclipIcon, { className: "size-4" }) }))] }), isStreaming && onStop && !value.trim() ? (_jsxs(Button, { onClick: handleStop, disabled: isStopping, className: "px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white", title: t('agent.stopAgent'), children: [isStopping ? _jsx(Spinner, { size: "sm", className: "mr-2" }) : _jsx(StopCircleIcon, { className: "size-4 mr-2" }), " ", _jsx("span", { children: t('agent.stop') })] })) : (_jsxs(Button, { onClick: handleSend, disabled: disabled || isSending || !value.trim() || hasProcessingFiles, className: "px-4 py-2.5", title: hasProcessingFiles ? t('agent.waitForFiles') : undefined, children: [isSending ? _jsx(Spinner, { size: "sm", className: "mr-2" }) : _jsx(SendIcon, { className: "size-4 mr-2" }), _jsx("span", { children: hasProcessingFiles ? t('agent.processing') : t('agent.send') })] }))] }), _jsx("div", { className: "text-xs text-muted mt-2 text-center", children: activeTaskCount > 0 ? (_jsxs("div", { className: "flex items-center justify-center", children: [_jsx(Activity, { className: "h-3 w-3 mr-1 text-attention" }), _jsx("span", { children: t('agent.activeWorkstreams', { count: activeTaskCount }) })] })) : isStreaming
                    ? t('agent.agentWorkingStop')
                    : disabled
                        ? t('agent.agentProcessing')
                        : t('agent.enterToSend') }), _jsxs(Modal, { isOpen: isObjectModalOpen, onClose: () => setIsObjectModalOpen(false), className: 'min-w-[60vw]', children: [_jsx(ModalTitle, { children: t('agent.linkObject') }), _jsx(ModalBody, { className: "pb-6", children: _jsx(SelectDocument, { onChange: handleObjectSelect }) })] }), renderDocumentSearch?.({ isOpen: isDocSearchOpen, onClose: handleDocSearchClose, onSelect: handleDocSearchSelect })] }));
}
//# sourceMappingURL=MessageInput.js.map